import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/lib/auth";
import { Logo } from "@/components/site/Navbar";
import { Field } from "@/components/site/bits";

const SIDE = "https://images.unsplash.com/photo-1583258292688-d0213dc5a3a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const AuthLayout = ({ title, sub, children, dark }) => (
  <div className={`min-h-screen grid lg:grid-cols-2 ${dark ? "bg-slate-950" : "bg-white"}`}>
    <div className="flex flex-col p-6 sm:p-10">
      <Logo />
      <div className="flex-1 flex items-center justify-center py-10">
        <div className={`w-full max-w-md space-y-6 ${dark ? "bg-white rounded-3xl p-8" : ""}`}>
          <div><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1><p className="text-sm text-slate-500 mt-1">{sub}</p></div>
          {children}
        </div>
      </div>
    </div>
    <div className="hidden lg:block relative">
      <img src={SIDE} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
      <p className="absolute bottom-10 left-10 right-10 font-heading text-4xl font-extrabold text-white leading-tight">Discover nearby deals.<br />Claim in seconds.</p>
    </div>
  </div>
);

const Submit = ({ busy, children, testid }) => (
  <button data-testid={testid} disabled={busy} className="w-full h-12 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
    {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
  </button>
);

function LoginForm({ portal }) {
  const { login } = useAuth();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await login(f.identifier, f.password, portal);
      const next = sp.get("next");
      nav(next && next.startsWith("/") ? next : homeFor(d.user.role));
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label={portal === "admin" ? "Admin email" : "Email or mobile number"}><Input data-testid="login-identifier-input" required value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} /></Field>
      <Field label="Password"><Input data-testid="login-password-input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
      <Submit busy={busy} testid="login-submit-button">Log in</Submit>
    </form>
  );
}

export function LoginPage() {
  return (
    <AuthLayout title="Welcome back" sub="Customers and merchants log in here.">
      <LoginForm />
      <div className="text-sm text-slate-600 space-y-1">
        <p>New here? <Link data-testid="goto-register" to="/register" className="font-bold text-red-500">Create a customer account</Link></p>
        <p>Own a store? <Link data-testid="goto-seller-register" to="/seller/register" className="font-bold text-red-500">Register as a seller</Link></p>
      </div>
    </AuthLayout>
  );
}

export function AdminLoginPage() {
  return (
    <AuthLayout dark title="Super Admin" sub="Restricted access. All actions are audit-logged.">
      <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg p-2"><ShieldCheck className="w-4 h-4" /> Secure admin portal</div>
      <LoginForm portal="admin" />
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { applySession } = useAuth();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      applySession((await api.post("/auth/register", { ...f, phone: f.phone || null })).data);
      toast.success("Account created");
      const next = sp.get("next");
      nav(next && next.startsWith("/") ? next : "/");
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <AuthLayout title="Create your account" sub="Claim flash deals from stores near you.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name"><Input data-testid="register-name-input" required value={f.name} onChange={set("name")} /></Field>
        <Field label="Email"><Input data-testid="register-email-input" type="email" required value={f.email} onChange={set("email")} /></Field>
        <Field label="Mobile number (optional)"><Input data-testid="register-phone-input" value={f.phone} onChange={set("phone")} placeholder="+919876543210" /></Field>
        <Field label="Password" hint="At least 8 characters"><Input data-testid="register-password-input" type="password" required minLength={8} value={f.password} onChange={set("password")} /></Field>
        <Submit busy={busy} testid="register-submit-button">Create account</Submit>
      </form>
      <p className="text-sm text-slate-600">Already registered? <Link to={`/login${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="font-bold text-red-500">Log in</Link></p>
    </AuthLayout>
  );
}
